import writtenNumber from 'written-number';
import currencyFormatter from 'currency-formatter';

const getListString = list => {
  let listString = '';
  list.map((item, index) => {
    let delimiter = '';
    if (list.length > 1 && index < list.length - 1) {
      if (index === list.length - 2) {
        delimiter = list.length === 2 ? ' or ' : ', or ';
      } else {
        delimiter = ', ';
      }
    }
    listString = listString + item + delimiter;
    return item;
  });
  return listString;
};

export default variables => `
<p>
  This Disc-Jockey Agreement is entered into as of the date in the
  Signatures block (“Effective Date”) by and between the Vendor (“DJ”),
  and Client (“Client”) as both are listed in the Signatures block
  (hereinafter collectively, “parties” and individually, a “party”).
  This Disc-Jockey Agreement, together with the Schedule, Line Items and
  Signatures, collectively form the “Agreement.”
</p>
<br />
<p>The parties to this Agreement agree as follows:</p>
<br />
<p>
  <strong>DEFINITIONS</strong>
  <ol>
    <li>
      “Project” or “Services” refers to the disc-jockey and related
      services described in the Line Items that Client is specifically
      commissioning DJ to perform.
    </li>
    <li>
      “Cancellation” means that the Project is cancelled by Client and
      not rescheduled. In the event of cancellation, Client will be
      responsible for such fees, costs and charges as set forth herein.
    </li>
    <li>
      “Postponement” is the rescheduling of the Project by the Client at
      least ${variables.postponementBeforeDays} days prior to the Project
      to a mutually agreeable date, and no more than
      ${variables.postponementAfterDays} calendar days after the original
      Project date. In the event of Postponement, Client will be
      responsible for such fees, costs and charges as set forth herein.
    </li>
  </ol>
</p>
<p>
  <strong>PAYMENT TERMS AND CONDITIONS</strong>
  <ol>
    <li>
      <strong>Fees.</strong> As compensation for the Services, Client
      agrees to pay the fees as set forth and agreed upon in the Line
      Items.
    </li>
    <li>
      <strong>Deposit.</strong> A non-refundable deposit may be required
      by DJ to hold scheduled event dates. The deposit will be due upon
      receipt of deposit invoice and prior to the commencement of the
      Services.
    </li>
    <li>
      <strong>Estimates.</strong> DJ will provide the Client with a cost
      estimate upon request. The fees and expenses estimated by DJ are
      for the original job description as presented by the Client and
      detailed in the Line Items. Subsequent changes or actual job
      conditions may result in additional charges as further described
      in the section below entitled Additional Expenses.
    </li>
    <li>
      <strong>Payment.</strong> Payments may be made by
      ${getListString(variables.paymentMethods)}.
    </li>
    <li>
      <strong>Billing.</strong> Payments shall be due on the date
      specified in the payment plan. In the absense of a payment plan,
      deposits are due immediately upon booking and the remaining
      balance must be paid ${writtenNumber(variables.paymentDaysBefore)}
      (${variables.paymentDaysBefore}) days prior to the commencement
      of the Services. All payments will be made withoutsetoff,
      counterclaim, recourse or other defense.
    </li>
    <li>
      <strong>Late Payments.</strong> DJ may assess Client a late
      payment charge on any amount which remains unpaid
      ${writtenNumber(
        variables.paymentGracePeriod
      )} (${variables.paymentGracePeriod})
      days after it is due, computed at the rate of
      ${writtenNumber(variables.interest)} percent (${variables.interest}%)
      per month or the highest allowable by law, whichever is lower, on
      the unpaid amount for every month the amount remains unpaid. In
      the event the Client fails to make timely payment, Client will be
      responsible for all reasonable expenses (including attorneys’
      fees) incurred by DJ in collecting such amounts. Nothing mentioned
      herein will limit any additional rights and remedies available to
      DJ at law, in equity and / or otherwise arising due to the default
      of payment by the Client.
    </li>
    <li>
      <strong>Expenses.</strong> Client will promptly reimburse DJ for
      any reasonable business or travel expenses, including but not
      limited to parking, tolls, permits and meals incurred in
      connection with the provision of Services. DJ will provide
      receipts to Client for such expenses. Any event over
      ${writtenNumber(variables.travelRadius)} (${variables.travelRadius})
      miles from ${variables.travelOrigin} may be assessed a travel fee
      and require overnight accommodations at Client’s expense. ${variables.mealRequired
        ? `Client agrees to provide meals at the event as well as adequate time for
        the meal (i.e. during the regular meal). For events longer than
        eight (8) hours, two (2) meals will be provided.`
        : ''}
    </li>
    <li>
      <strong>Taxes.</strong> Client will be responsible for, and agrees
      to pay, all taxes imposed by the United States, any state or local
      government, or other taxing authority, on all goods and/or
      services provided under this Agreement.
    </li>
  </ol>
</p>
<p>
  <strong>ADDITIONAL EXPENSES</strong>
  <ol>
    <li>
      Client shall be responsible for any additional expenses and fees
      that result from any requests by Client for changes or additions
      to the Project. Changes or additions should be made in advance, in
      writing (email acceptable), and may require the parties to add
      additional Line Items. However, Client understands and agrees that
      written approvals of modifications to the Project or a written
      estimate of fees for the Services may not be possible due to the
      time constraints. As such, Clients shall be bound by any oral
      instructions provided to DJ or any of its representatives by
      Client or any of Client’s representatives at the time of the
      Project or thereafter. While the DJ cannot guarantee availability
      to perform longer than the agreed upon timeframe, if possible, the
      DJ’s overtime hours will be billed at ${currencyFormatter.format(
        variables.overtimeRate,
        { code: variables.overtimeCurrency }
      )} per
      half-hour.
    </li>
  </ol>
</p>
<p>
  <strong>RESPONSIBILITIES</strong>
  <ol>
    <li>
      Each party represents and warrants that it has full power and
      authority to enter into and perform this Agreement.
    </li>
    <li>
      The Client(s) signing the contract will be the only person(s) with
      authority over event decisions, including music choices,
      timelines, etc.
    </li>
    <li>
      Client is to provide addresses to all locations, emergency
      telephone numbers, an itinerary of the day, special toasts,
      readings and requested play lists or songs at least
      ${writtenNumber(
        variables.planningDeadline
      )} (${variables.planningDeadline})
      ${variables.planningDeadlineUnits}
      prior to the event. DJ shall make reasonable efforts to play music
      requests, but shall not be held responsible if certain selections
      are unavailable, including those that cannot be legally obtained.
      The DJ will make extra effort to have music requests available
      that are received in writing at least
      ${writtenNumber(
        variables.planningDeadline
      )} (${variables.planningDeadline})
      ${variables.planningDeadlineUnits} prior to the event. The DJ
      reserves the right to deny inappropriate/explicit requests from
      both the Client and the Client’s guests.
    </li>
    <li>
      The DJ will not be held accountable for any failure to play a
      specific song.
    </li>
    <li>
      ${variables.outdoorSurcharge
        ? `Outdoor events may be subject to additional fees due to increased
        labor costs and risk of damage to equipment.`
        : ''}
        The DJ reserves the right to protect DJ’s equipment and not perform
        if weather conditions pose a threat to the safety of the DJ or DJ’s
        equipment.
    </li>
    <li>
      While the Parties agree to work together, the Client agrees to
      grant the DJ final control of the entertainment timeline.
    </li>
  </ol>
</p>
<p>
  <strong>VENUE</strong>
  <ol>
    <li>
      The DJ is limited by the rules and guidelines of the location(s)
      and site management. The Client agrees to accept the technical
      results of their imposition on the DJ. Negotiation with the
      officials for moderation of guidelines is the Client’s
      responsibility; the DJ will offer technical recommendations only.
    </li>
    <li>
      The Client is responsible for acquiring all permits and necessary
      permission for all locations on which the DJ will be performing
      services.
    </li>
    <li>
      ${variables.setupInstructionsRequired ? variables.setupInstructions : ''}
      Client will be solely responsible for any additional fees charged by
      the venue, including fees for power supply, and other set-up, loading and
      unloading of DJ equipment.
    </li>
    <li>
      It is the client’s responsibility to coordinate with the venue to
      provide for proper setup and breakdown of the DJ equipment as well
      as proper access for transport, as needed, via stairs, ramp and/or
      elevator. There may be additional labor fees if there is no
      elevator or ramp access.
    </li>
    <li>
      The DJ reserves to right to leave the location of the event if the
      DJ experiences inappropriate, threatening, hostile or offensive
      behavior from person(s) at the event; or in the event that the
      safety of the DJ is in question.
    </li>
  </ol>
</p>
<p>
  <strong>
    LATENESS, POSTPONEMENT, CANCELLATION & LIQUIDATED DAMAGES
  </strong>
  <ol>
    <li>
      <strong>Lateness.</strong> If the Client is late and/or delays an
      event, the Client will be responsible for payment at DJ’s standard
      hourly rates for such period.
    </li>
    <li>
      <strong>Postponement.</strong> Client shall provide DJ with notice
      of a Postponement at least
      ${writtenNumber(
        variables.postponementBeforeDays
      )} (${variables.postponementBeforeDays})
      days prior to the scheduled Project. In the event that Client
      postpones less than ${writtenNumber(variables.postponementBeforeDays)}
      (${variables.postponementBeforeDays}) days prior to the scheduled Project,
      Client shall pay to DJ a Postponement fee in the amount of
      ${writtenNumber(
        variables.postponementPenalty
      )} percent (${variables.postponementPenalty}%)
      of the Fees plus any expenses incurred by DJ. Client agrees to be
      solely responsible for all expenses incurred by DJ to reschedule
      the Project due to Client’s Postponement, regardless of whether or
      not Client provided ${writtenNumber(variables.postponementBeforeDays)}
      (${variables.postponementBeforeDays}) days’ notice.
    </li>
    <li>
      <strong>Cancellation.</strong> Therefore, Client agrees to notify
      DJ of a Cancellation at least
      ${writtenNumber(
        variables.cancellationBeforeDays
      )} (${variables.cancellationBeforeDays})
      days prior to the Project commencement date in order to minimize
      DJ’s costs, expenses, and loss of income. In the event that Client
      cancels this Agreement at least
      ${writtenNumber(
        variables.cancellationBeforeDays
      )} (${variables.cancellationBeforeDays})
      days prior to the date set for commencement of the Project, Client
      agrees to pay a Cancellation fee in the amount of
      ${writtenNumber(
        variables.cancellationPenalty
      )} percent (${variables.cancellationPenalty}%)
      of the Fees in addition to all other costs incurred by DJ. Due to
      the inability of DJ to perform other services during time reserved
      for Client, if DJ receives notification of Client’s Cancellation
      of the Project hereunder with less than
      ${writtenNumber(
        variables.cancellationBeforeDays
      )} (${variables.cancellationBeforeDays})
      days’ notice, Client shall be responsible for ${writtenNumber(
        variables.lateCancellationPenalty
      )}
      percent (${variables.lateCancellationPenalty}%) of the Fees.
      Said Cancellation fees are designed to cover
      the loss that DJ would not otherwise have incurred had the Project
      been commenced as scheduled. Client also agrees to bear sole
      responsibility for any expenses incurred by DJ as a result of
      Client’s Cancellation of this Agreement at any time.
    </li>
  </ol>
</p>
<p>
  <strong>TERMINATION</strong>
  <ol>
    <li>
      Either party may terminate this Agreement upon fifteen (15) days
      written notice if the other party materially breaches any
      provisions of this Agreement, and such breach has not been cured
      after notice of the same within such fifteen (15) day period.
    </li>
    <li>
      DJ may suspend performance under this Agreement immediately upon
      written notice to Client if Client neglects to make any required
      payments when due.
    </li>
    <li>
      If this Agreement is terminated for any reason, the rights of the
      Client arising under the Agreement shall terminate with immediate
      effect but the obligations of the Client that by their nature are
      meant to survive (i.e. unpaid Fees), shall survive such
      termination.
    </li>
  </ol>
</p>
<p>
  <strong>WARRANTY</strong>
  <ol>
    <li>
      Each party represents and warrants that: (a) it has full power and
      authority to enter into this Agreement; (b) the person signing
      this Agreement on such party’s behalf has been duly authorized and
      empowered to enter into this Agreement and (d) that it will
      perform its obligations or exercise its rights hereunder in
      conformance with all applicable laws, rules, regulations and
      guidelines.
    </li>
    <li>
      DJ hereby represents, warrants and covenants to Client that DJ
      will perform the Services in a professional and workmanlike manner
      and in accordance with all reasonable professional standards for
      such services.
    </li>
    <li>
      CLIENT UNDERSTANDS AND AGREES THAT, EXCEPT AS OTHERWISE SET FORTH
      HEREIN, THE SERVICES AND WORK ARE PROVIDED ON AN “AS IS” AND “AS
      AVAILABLE” BASIS ONLY, AND USE OF DJ’S SERVICES IS AT ITS OWN
      RISK. EXCEPT AS OTHERWISE SET FORTH HEREIN, DJ DOES NOT MAKE AND
      HEREBY DISCLAIMS ANY AND ALL EXPRESS, IMPLIED OR STATUTORY
      WARRANTIES, CONDITIONS, REPRESENTATIONS, INCLUDING BUT NOT LIMITED
      TO, THE IMPLIED WARRANTIES OF TITLE, MERCHANTABILITY, FITNESS FOR
      A PARTICULAR PURPOSE, ACCURACY, TIMELINESS, COMPLETENESS,
      COMPLIANCE WITH LAW, ABSENCE OF VIRUSES OR OTHER MALICIOUS
      SOFTWARE, ADEQUACY AND NON-INFRINGEMENT OR WARRANTY ARISING OUT OF
      COURSE OF PERFORMANCE, COURSE OF DEALING OR USAGE OR TRADE
      PRACTICE.
    </li>
    <li>
      Client agrees that DJ has made no agreements, representations or
      warranties other than those expressly set forth in this Agreement,
      and that no future agreement, representation or warranty of DJ
      with regard to Services provided under this agreement shall be
      effective unless expressly stated in an amendment to this
      Agreement signed by both parties.
    </li>
  </ol>
</p>
<p>
  <strong>LIMITATION OF LIABILITY</strong>
  <ol>
    <li>
      It is expressly agreed that in no event shall DJ be liable for any
      special, indirect, consequential, or exemplary damages.
    </li>
    <li>
      DJ’s sole and exclusive liability and Client’s sole and exclusive
      remedy for all claims of defects in the Services will be, in DJ’s
      sole discretion, to either: (A) re-perform such Services; or (B)
      partially credit or refund the Fees paid by Client for such
      Services.
    </li>
    <li>
      In the unlikely event of unforeseen illness or any other
      circumstances beyond DJ’s control, a substitute disc-jockey of
      high qualifications, may be dispatched by DJ (if approved by
      Client).
    </li>
    <li>
      Because some states or jurisdictions do not allow the exclusion or
      the limitation of liability for consequential or incidental
      damages, in such states or jurisdictions, DJ’s liability shall be
      limited to the maximum extent permitted by law.
    </li>
  </ol>
</p>
<p>
  <strong>INDEMNIFICATION</strong>
  <ol>
    <li>
      Client agrees to indemnify, defend and hold harmless DJ, and its
      directors, officers, employees, agents and representatives from
      and against any and all losses, damages, demands, claims, costs,
      penalties, injuries, interest, or expenses (including without
      limitation reasonable attorney fees) (“Losses”) arising out of or
      relating to: (i) any breach of any warranty, representation,
      covenant or agreement made by Client in this Agreement, (ii) any
      violation of federal, state or local laws, rules or regulations,
      or (iii) any act or omission (negligent, willful or otherwise) or
      misconduct by Client, its directors, officers, Clients, employees,
      contractors, or agents.
    </li>
    <li>
      If any equipment used by DJ during the event is damaged or
      destroyed by Client, any of Client's invitees, guests, affiliates,
      friends or family members or any other person hired by Client in
      connection with the event, Client shall reimburse DJ for 100% of
      the costs to fix such damage, including insurance deductibles or
      in the case of destruction 100% of the replacement cost of such
      equipment.
    </li>
  </ol>
</p>
<p>
  <strong>MISCELLANEOUS PROVISIONS</strong>
  <ol>
    <li>
      <strong>Independent Contractor:</strong> The parties to the
      Agreement are independent contractors and nothing in the Agreement
      shall be deemed to make either party an agent, employee, or
      partner of the other party. Neither party shall have any authority
      to bind, commit, or otherwise obligate the other party in any
      manner whatsoever.
    </li>
    <li>
      <strong>Publicity:</strong> The DJ may take videos and/or
      photographs at the event and use such for its own business or
      promotional purposes in any medium, including DJ’s website, social
      media platforms, advertisements, etc.
    </li>
    <li>
      <strong>Entire Agreement:</strong> This Agreement constitutes the
      entire agreement between the parties regarding the subject matter
      hereof and supersedes all proposals, prior discussions and
      writings between the parties with respect thereto.
    </li>
    <li>
      <strong>Governing Law:</strong> This Agreement shall be governed
      by and construed under the laws of the State of ${variables.state}
      without regard to its conflict of law principles. The parties
      agree that the application of the United Nations Convention on
      Contracts for the International Sale of Goods does not apply to
      this Agreement and is strictly excluded. The Parties irrevocably
      consent and submit to personal jurisdiction in the state and
      federal courts of ${variables.county} County, ${variables.state},
      for all matters arising under this Agreement.
    </li>
    <li>
      <strong>Arbitration:</strong> If any dispute, controversy or claim
      arises between the parties with respect to this Agreement, the
      parties agree that they will submit the matter to binding
      arbitration before a single arbitrator with the American
      Arbitration Association in ${variables.county} County, in
      accordance with the applicable rules promulgated by the American
      Arbitration Association as permitted by the laws of the State of
      ${variables.state}. The prevailing party in such arbitration shall
      be awarded, in addition to costs, reasonable attorney’s fees as
      fixed by the arbitrator(s). Full rights of discovery, including
      the right to depose, shall be allowed in the course of such
      arbitration. The decision of the arbitrator will be final and
      nonappealable and may be enforced according to the laws of the
      State of ${variables.state}.
    </li>
    <li>
      <strong>Expenses and Attorneys’ Fees:</strong> In the event any
      action is brought to enforce any provision of the Agreement or to
      declare a breach of the Agreement, the prevailing party shall be
      entitled to recover, in addition to any other amounts awarded,
      reasonable legal and other related costs and expenses, including
      attorney’s fees, incurred thereby.
    </li>
    <li>
      <strong>Modifications and Waivers:</strong> This Agreement may not
      be modified except by a writing signed by authorized
      representatives of both parties. A waiver by any party of its
      rights under this Agreement shall not be binding unless contained
      in a writing signed by an authorized representative of the party
      waiving its rights. The non-enforcement or waiver of any provision
      on any occasion shall not constitute a waiver of such provision on
      any other occasions unless expressly so agreed in writing. It is
      agreed that no usage of trade or other regular practice or method
      of dealing between or among the parties to this Agreement shall be
      used to modify, interpret, supplement, or alter in any manner the
      terms of this Agreement.
    </li>
    <li>
      <strong>Severability:</strong> It is understood and agreed by the
      parties to this Agreement that it is their intention that if a
      court of competent jurisdiction shall determine that any of the
      terms of this Agreement are invalid or otherwise unenforceable,
      that such court shall substitute terms, therefore, with such court
      determines are enforceable, so as to result in the enforcement of
      the original terms to the maximum extent permitted by law.
    </li>
    <li>
      Notices: Any notice, approval or consent required or permitted
      under this Agreement shall be in writing and sent to the email
      addresses set forth in the Signatures block.
    </li>
    <li>
      <strong>No Third party Beneficiaries:</strong> Nothing express or
      implied in this Agreement is intended to confer, nor shall confer,
      upon any person or entity other than the parties, their licensors,
      and their respective successors or assigns any rights, remedies,
      obligations, or liabilities whatsoever.
    </li>
    <li>
      <strong>Assignment:</strong> Neither party may assign this
      Agreement without the prior written consent of the other party.
      Notwithstanding the foregoing, This Agreement shall be binding
      upon and shall benefit the parties and their respective successors
      and permitted assigns.
    </li>
    <li>
      <strong>Interpretation:</strong> Section headings are for
      reference only, and shall not be construed as substantive parts of
      this Agreement. Each capitalized term used in this Agreement
      (including any schedule or exhibit of this Agreement) shall have
      the meaning attributed to it in any part of this Agreement
      (including any such Line Items, schedules or exhibits).
    </li>
    <li>
      <strong>Survival:</strong> Client acknowledges and agrees that all
      provisions which by their terms contemplate survival shall survive
      the termination or expiration of this Agreement regardless of the
      cause of such termination.
    </li>
    <li>
      <strong>Force Majeure:</strong> Neither party shall be liable to
      the other party for any delay or failure of said party to perform
      its obligations hereunder (except for payment obligations) if such
      delay or failure arises from any cause or causes beyond the
      reasonable control of such party. Such causes shall include, but
      are not limited to, acts of God, floods, fires, theft, loss of
      electricity or other utilities.
    </li>
    <li>
      <strong>Counterparts:</strong> This Agreement may be signed in
      several counterparts, and all counterparts so executed shall
      constitute one Agreement, binding upon the parties hereto.
    </li>
    <li>
      <strong>Electronic Document:</strong> This Agreement may be signed
      electronically using a means reasonably designed to acknowledge
      assent. If this Agreement is electronically signed, it: (a) is
      considered a "writing" or "in writing," (b) is deemed for all
      purposes as physically "signed," (c) is deemed an "original" when
      printed or copied from electronic files or records established and
      maintained in the normal course of business, and (d) satisfies any
      legal formalities requiring that agreements be in writing. Neither
      Party shall contest the admissibility of copies of this Agreement
      (or printed version of same) under either the business records
      exception to the hearsay rule or the best evidence rule or
      otherwise on the basis that the Agreement was originated, signed
      or maintained in electronic form. Other than an original hand
      written signature or an electronic signature of the same formality
      used to originally execute this Agreement, no other communication
      between the Parties (such as email, voice mail or fax without a
      signature) shall be construed as a signature to this Agreement (or
      any amendments hereto or any waiver hereof).
    </li>
  </ol>
</p>
`;



// WEBPACK FOOTER //
// ./webpack/components/Document/Sections/TermsBuilder/DJContract.js